import { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from 'formik';
import * as Yup from "yup";
import { Autocomplete, Box, Button, Chip, CircularProgress, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Typography, IconButton } from "@mui/material";
import { ConfirmationNumberOutlined, DeleteOutline } from "@mui/icons-material";
import { ccyFormat } from "../../helpers/helper";
import StoreCreditService from "../../services/storeCredits";
import { STATUS_STORE_CREDIT } from "../../helpers/enums";

const formSchema = Yup.object().shape({
  payments: Yup.array()
    .of(
      Yup.object().shape({
        total: Yup.number()
          .required("El total es requerido")
          .min(0, "El total debe ser mayor o igual a 0"),
        amount: Yup.number()
          .required("El monto es requerido")
          .min(0, "El monto debe ser mayor o igual a 0")
          .test(
            "amount-equals-total",
            "El monto debe ser igual al total",
            function (value) {
              return value === this.parent.base_total;
            }
          ),
        paymenttypes: Yup.array()
          .of(
            Yup.object().shape({
              _id: Yup.string()
                .required("El ID del tipo de pago es requerido"),
              amount: Yup.number()
                .required("El monto es requerido")
                .min(0, "El monto debe ser mayor o igual a 0"),
            })
          )
          .required("Debe haber al menos un tipo de pago"),
      })
    )
    .required("Debe haber al menos un pago"),
});



const FormPaymentType = ({ payments, onSubmit, onClose, storeId }) => {
  const firstInputRef = useRef(null);
  let initialValues = {
    payments: payments.map((p) => ({ ...p, base_total: p.total, credit: p.credit || null })),
  };
  const [creditCodeInputs, setCreditCodeInputs] = useState({});
  const [creditLoading, setCreditLoading] = useState({});
  const [creditErrors, setCreditErrors] = useState({});
  const [availableCredits, setAvailableCredits] = useState([]);
  const [loadingCredits, setLoadingCredits] = useState(false);

  useEffect(() => {
    if (!storeId) return;
    let active = true;
    setLoadingCredits(true);
    StoreCreditService.getAll(storeId, STATUS_STORE_CREDIT.PENDING, undefined, 1, 200)
      .then((resp) => { if (active) setAvailableCredits(Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp) ? resp : [])); })
      .catch(() => { if (active) setAvailableCredits([]); })
      .finally(() => { if (active) setLoadingCredits(false); });
    return () => { active = false; };
  }, [storeId]);

  const handleSubmitForm = async (data) => {
    if (onSubmit) await onSubmit(data);
  };

  const { handleSubmit, setFieldValue, values, errors, isValid, isSubmitting } = useFormik({
    initialValues,
    onSubmit: handleSubmitForm,
    validationSchema: formSchema,
  });

  useEffect(() => {
    setTimeout(() => firstInputRef.current?.focus(), 50);
  }, []);

  const paymentTypeTotals = useMemo(() => {
    const totals = {};
    (values?.payments || []).forEach((payment) => {
      (payment?.paymenttypes || []).forEach((pt) => {
        const key = pt?._id || pt?.name;
        if (!key) return;
        if (!totals[key]) {
          totals[key] = {
            key,
            name: pt?.name || 'Medio de pago',
            amount: 0,
          };
        }
        totals[key].amount += Number(pt?.amount || 0);
      });
    });
    return Object.values(totals);
  }, [values]);

  const grandTotal = useMemo(() => (
    (values?.payments || []).reduce((sum, payment) => sum + Number(payment?.total || 0), 0)
  ), [values]);

  const grandCreditsTotal = useMemo(() => (
    (values?.payments || []).reduce((sum, payment) => sum + Number(payment?.credit?.amount || 0), 0)
  ), [values]);

  const grandAssigned = useMemo(() => (
    (values?.payments || []).reduce((sum, p) => sum + Number(p?.amount || 0), 0)
  ), [values]);

  // Pending compares against base_total (already net of any vale applied), not the full total.
  const grandPending = Math.max(0, (values?.payments || []).reduce((sum, p) => sum + Number(p?.base_total || 0), 0) - grandAssigned);

  const usedCreditCodes = useMemo(() => {
    const codes = new Set();
    (values?.payments || []).forEach((payment) => {
      if (payment?.credit?.code) codes.add(payment.credit.code);
    });
    return codes;
  }, [values]);

  const normalizeInstallments = (paymentType = {}) => {
    if (!Array.isArray(paymentType?.installments)) return [];
    return paymentType.installments
      .filter((i) => Number(i?.quantity) > 0)
      .map((i) => ({
        quantity: Number(i.quantity),
        interest_rate: Number(i.interest_rate || 0),
        label: i.label || `${i.quantity} cuota${Number(i.quantity) !== 1 ? 's' : ''}`,
      }))
      .sort((a, b) => a.quantity - b.quantity);
  };

  const getDefaultInstallment = (paymentType = {}) => {
    const installments = normalizeInstallments(paymentType);
    if (!installments.length) return null;
    return installments.find((i) => Number(i.quantity) === 1) || installments[0];
  };

  const getSelectedInstallment = (paymentType = {}) => {
    const installments = normalizeInstallments(paymentType);
    if (!installments.length) return null;

    const selectedQuantity = Number(paymentType?.selected_installment?.quantity || 0);
    const selectedByQuantity = installments.find((i) => i.quantity === selectedQuantity);
    if (selectedByQuantity) return selectedByQuantity;

    return getDefaultInstallment(paymentType);
  };

  // Computa recargo y monto efectivo (base + interés) para un array de payment types
  const computeEffective = (pts) => {
    let surcharge = 0;
    let effectiveAmount = 0;
    (pts || []).forEach((pt) => {
      const base = Number(pt?.amount || 0);
      const inst = getSelectedInstallment(pt);
      const rate = Number(inst?.interest_rate || 0);
      const s = base * (rate / 100);
      surcharge += s;
      effectiveAmount += base + s;
    });
    return { surcharge, effectiveAmount };
  };

  const simulationTotals = useMemo(() => {
    let surcharge = 0;
    let financed = 0;

    (values?.payments || []).forEach((payment) => {
      (payment?.paymenttypes || []).forEach((pt) => {
        const amount = Number(pt?.amount || 0);
        if (amount <= 0) return;

        const selectedInstallment = getSelectedInstallment(pt);
        const rate = Number(selectedInstallment?.interest_rate || 0);
        const surchargeAmount = amount * (rate / 100);
        surcharge += surchargeAmount;
        financed += amount + surchargeAmount;
      });
    });

    return {
      surcharge,
      financed,
    };
  }, [values]);

  const hasInterest = simulationTotals.surcharge > 0;
  const grandBaseTotal = useMemo(() => (
    (values?.payments || []).reduce((sum, p) => sum + Number(p?.base_total || 0), 0)
  ), [values]);

  const applyAll = (paymentTypeKey) => {
    const updatedPayments = values.payments.map((payment) => {
      const updatedPts = payment.paymenttypes.map((p) => {
        const key = p?._id || p?.name;
        return { ...p, amount: key === paymentTypeKey ? payment.base_total : 0 };
      });
      return { ...payment, paymenttypes: updatedPts, amount: payment.base_total };
    });
    setFieldValue('payments', updatedPayments);
  };

  const findPaymentTypeKeyByName = (name) => {
    const normalizedName = (name || '').toLowerCase();
    const firstPayment = values?.payments?.[0];
    const found = (firstPayment?.paymenttypes || []).find((pt) =>
      (pt?.name || '').toLowerCase().includes(normalizedName)
    );
    return found ? (found._id || found.name) : null;
  };

  // Atajos: E/T si esos medios existen, Escape cierra
  useEffect(() => {
    const handle = (e) => {
      if (e.target?.tagName === 'INPUT') return;
      if (e.key === 'Escape') { e.preventDefault(); onClose && onClose(); return; }
      if (e.key === 'e' || e.key === 'E') {
        const key = findPaymentTypeKeyByName('efectivo');
        if (key) {
          e.preventDefault();
          applyAll(key);
        }
        return;
      }
      if (e.key === 't' || e.key === 'T') {
        const key = findPaymentTypeKeyByName('transferencia');
        if (key) {
          e.preventDefault();
          applyAll(key);
        }
        return;
      }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const handleAmountChange = (paymentIndex, ptIndex, newValue) => {
    const updatedPayments = values.payments.map((payment, pi) => {
      if (pi !== paymentIndex) return payment;
      const updatedPts = payment.paymenttypes.map(
        (item, i) => i === ptIndex ? { ...item, amount: newValue } : item
      );
      const baseSum = updatedPts.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      return { ...payment, paymenttypes: updatedPts, amount: baseSum };
    });
    setFieldValue('payments', updatedPayments);
  };

  // Clips the already-assigned paymenttypes amounts down to the new (lower) base_total.
  const clipPaymentTypesToBaseTotal = (paymenttypes, newBaseTotal) => {
    let remainingCap = newBaseTotal;
    const clipped = paymenttypes.map((pt) => {
      const amt = Math.min(Number(pt.amount || 0), Math.max(remainingCap, 0));
      remainingCap -= amt;
      return { ...pt, amount: amt };
    });
    const newAssigned = clipped.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    return { paymenttypes: clipped, amount: newAssigned };
  };

  // A vale is a direct discount on the sale total (store_credit_applied), not a payment method.
  const handleAddCreditDiscount = (paymentIndex, credit) => {
    if (!credit || credit.status !== STATUS_STORE_CREDIT.PENDING) {
      setCreditErrors((prev) => ({ ...prev, [paymentIndex]: `El vale está ${credit?.status || 'no disponible'}` }));
      return;
    }

    const payment = values.payments[paymentIndex];
    if (payment.credit) {
      setCreditErrors((prev) => ({ ...prev, [paymentIndex]: 'Ya hay un vale aplicado, quitalo para cambiarlo' }));
      return;
    }

    const total = Number(payment.total || 0);
    const balance = Number(credit.balance ?? credit.amount ?? 0);
    const amount = Math.min(balance, total);
    const newBaseTotal = Math.max(total - amount, 0);
    const { paymenttypes: clippedTypes, amount: newAssigned } = clipPaymentTypesToBaseTotal(payment.paymenttypes, newBaseTotal);

    const updatedPayments = values.payments.map((p, pi) => pi !== paymentIndex ? p : {
      ...p,
      credit: { code: credit.code, credit_id: credit._id, max_amount: balance, amount },
      base_total: newBaseTotal,
      paymenttypes: clippedTypes,
      amount: newAssigned,
    });
    setFieldValue('payments', updatedPayments);
    setCreditErrors((prev) => ({ ...prev, [paymentIndex]: '' }));
    setCreditCodeInputs((prev) => ({ ...prev, [paymentIndex]: '' }));
  };

  const handleCreditAmountChange = (paymentIndex, newValue) => {
    const payment = values.payments[paymentIndex];
    const credit = payment.credit;
    if (!credit) return;

    const total = Number(payment.total || 0);
    const amount = Math.min(Math.max(newValue, 0), Number(credit.max_amount || 0), total);
    const newBaseTotal = Math.max(total - amount, 0);
    const { paymenttypes: clippedTypes, amount: newAssigned } = clipPaymentTypesToBaseTotal(payment.paymenttypes, newBaseTotal);

    const updatedPayments = values.payments.map((p, pi) => pi !== paymentIndex ? p : {
      ...p,
      credit: { ...credit, amount },
      base_total: newBaseTotal,
      paymenttypes: clippedTypes,
      amount: newAssigned,
    });
    setFieldValue('payments', updatedPayments);
  };

  // The list endpoint may return a stale/incomplete balance, so re-fetch the
  // vale by code before adding it to get its authoritative current balance.
  const handleSelectCredit = async (paymentIndex, listItem) => {
    if (!listItem?.code || !storeId) return;
    setCreditErrors((prev) => ({ ...prev, [paymentIndex]: '' }));
    setCreditLoading((prev) => ({ ...prev, [paymentIndex]: true }));
    try {
      const credit = await StoreCreditService.getByCode(storeId, listItem.code);
      handleAddCreditDiscount(paymentIndex, credit);
    } catch (err) {
      setCreditErrors((prev) => ({ ...prev, [paymentIndex]: 'Vale no encontrado' }));
    } finally {
      setCreditLoading((prev) => ({ ...prev, [paymentIndex]: false }));
    }
  };

  const handleSearchCredit = async (paymentIndex) => {
    const code = (creditCodeInputs[paymentIndex] || '').trim();
    if (!code || !storeId) return;

    setCreditErrors((prev) => ({ ...prev, [paymentIndex]: '' }));
    setCreditLoading((prev) => ({ ...prev, [paymentIndex]: true }));
    try {
      const credit = await StoreCreditService.getByCode(storeId, code);
      handleAddCreditDiscount(paymentIndex, credit);
    } catch (err) {
      setCreditErrors((prev) => ({ ...prev, [paymentIndex]: 'Vale no encontrado' }));
    } finally {
      setCreditLoading((prev) => ({ ...prev, [paymentIndex]: false }));
    }
  };

  const handleRemoveCreditDiscount = (paymentIndex) => {
    const updatedPayments = values.payments.map((p, pi) => pi !== paymentIndex ? p : {
      ...p,
      credit: null,
      base_total: Number(p.total || 0),
    });
    setFieldValue('payments', updatedPayments);
  };

  const handleInstallmentChange = (paymentIndex, ptIndex, quantityValue) => {
    const paymentType = values?.payments?.[paymentIndex]?.paymenttypes?.[ptIndex];
    const installments = normalizeInstallments(paymentType);
    const selected = installments.find((i) => i.quantity === Number(quantityValue));
    if (!selected) return;
    setFieldValue(
      `payments[${paymentIndex}].paymenttypes[${ptIndex}].selected_installment`,
      selected
    );
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      p={{ xs: 1.25, sm: 2 }}
      gap={1.5}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && isValid) { e.preventDefault(); handleSubmit(); }
        if (e.key === 'Escape') { e.preventDefault(); onClose && onClose(); }
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ borderRadius: 2, p: 1.5, bgcolor: '#f5f7fa', border: '1px solid rgba(0,0,0,0.08)' }}>
        <Box display='flex' justifyContent='space-between' alignItems='flex-start' gap={1}>
          <Box>
            <Typography
              sx={{ fontSize: { xs: '2rem', sm: '2.35rem' }, fontWeight: 900, color: 'primary.main', lineHeight: 1 }}
            >
              ${ccyFormat(grandBaseTotal + simulationTotals.surcharge)}
            </Typography>
            {hasInterest && (
              <Typography variant='caption' color='text.secondary'>
                Base ${ccyFormat(grandBaseTotal)} + recargo ${ccyFormat(simulationTotals.surcharge)}
              </Typography>
            )}
            {grandCreditsTotal > 0 && (
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                Total venta ${ccyFormat(grandTotal)} · Vale aplicado -${ccyFormat(grandCreditsTotal)}
              </Typography>
            )}
          </Box>
          <Chip
            label={grandPending > 0 ? `Pendiente $${ccyFormat(grandPending)}` : 'Completo ✓'}
            color={grandPending > 0 ? 'error' : 'success'}
            size='small'
            sx={{ fontWeight: 700, mt: 0.5, flexShrink: 0 }}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box
          display='flex'
          gap={0.75}
          alignItems='center'
          flexWrap='wrap'
          sx={{ '& .MuiChip-root': { flexShrink: 0 } }}
        >
          <Typography variant='caption' color='text.secondary'>Todo en:</Typography>
          {paymentTypeTotals.map((pt) => (
            <Chip
              key={pt.key}
              size='small'
              label={pt.name}
              onClick={() => applyAll(pt.key)}
              variant='outlined'
              sx={{ bgcolor: '#fff', fontSize: '0.75rem', height: 26 }}
            />
          ))}
        </Box>
      </Box>

      {/* ── Payments ── */}
      {values?.payments?.map((payment, paymentIndex) => (
        <Box
          key={paymentIndex}
          sx={{
            borderRadius: 2,
            bgcolor: '#fff',
            border: '1px solid rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          {/* Row header */}
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            px={1.5}
            py={0.75}
            sx={{ bgcolor: '#f9fafb', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
          >
            <Typography variant='body2' fontWeight={700} sx={{ minWidth: 0, wordBreak: 'break-word' }}>
              {payment.nameSale || 'Venta'}
            </Typography>
            <Box textAlign='right' sx={{ flexShrink: 0, ml: 1 }}>
              <Typography variant='body2' fontWeight={700} color='primary.main'>
                ${ccyFormat(payment.base_total)}
              </Typography>
              {payment.credit && (
                <Typography variant='caption' color='text.secondary' sx={{ textDecoration: 'line-through' }}>
                  ${ccyFormat(payment.total)}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Payment type rows */}
          {payment.paymenttypes.map((pt, ptIndex) => {
            const selectedInstallment = getSelectedInstallment(pt);
            const rate = Number(selectedInstallment?.interest_rate || 0);
            const quantity = Number(selectedInstallment?.quantity || 1);
            const amount = Number(pt.amount || 0);
            const surcharge = amount * (rate / 100);
            const financedTotal = amount + surcharge;
            const installmentValue = quantity > 0 ? financedTotal / quantity : financedTotal;
            const hasInstallments = Boolean(pt?.applies_surcharge && normalizeInstallments(pt).length > 0);
            const showSummary = hasInstallments && amount > 0 && (quantity > 1 || rate > 0);
            const isLast = ptIndex === payment.paymenttypes.length - 1;

            return (
              <Box
                key={ptIndex}
                sx={{
                  px: 1.5,
                  pt: 1,
                  pb: showSummary ? 0.75 : 1,
                  borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Box display='flex' flexWrap='wrap' alignItems='center' gap={1}>
                  <Typography
                    variant='body2'
                    sx={{ flex: { xs: '1 0 100%', sm: '1 0 0' }, minWidth: 80, wordBreak: 'break-word' }}
                  >
                    {pt.name}
                  </Typography>
                  <TextField
                    inputRef={paymentIndex === 0 && ptIndex === 0 ? firstInputRef : undefined}
                    type='number'
                    size='small'
                    value={pt.amount === 0 ? '' : pt.amount}
                    onChange={(e) => {
                      const v = Math.max(0, Number(e.target.value) || 0);
                      handleAmountChange(paymentIndex, ptIndex, v);
                    }}
                    InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }}
                    inputProps={{ min: 0 }}
                    error={!!errors.payments?.[paymentIndex]?.paymenttypes?.[ptIndex]?.amount}
                    sx={{ width: 140, flexShrink: 0 }}
                  />
                  {hasInstallments && (
                    <FormControl size='small' sx={{ width: 170, flexShrink: 0 }}>
                      <InputLabel id={`installment-${paymentIndex}-${ptIndex}`}>Cuotas</InputLabel>
                      <Select
                        labelId={`installment-${paymentIndex}-${ptIndex}`}
                        value={String(selectedInstallment?.quantity || '')}
                        label='Cuotas'
                        onChange={(e) => handleInstallmentChange(paymentIndex, ptIndex, e.target.value)}
                      >
                        {normalizeInstallments(pt).map((ins) => (
                          <MenuItem key={`${ins.quantity}-${ins.interest_rate}`} value={String(ins.quantity)}>
                            {ins.label} ({ccyFormat(ins.interest_rate)}%)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                {showSummary && (
                  <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block', pl: 0 }}>
                    {quantity} cuotas de{' '}
                    <strong style={{ color: '#1a1a1a' }}>${ccyFormat(installmentValue)}</strong>
                    {rate > 0 && (
                      <> · total <strong style={{ color: '#1a1a1a' }}>${ccyFormat(financedTotal)}</strong></>
                    )}
                  </Typography>
                )}
              </Box>
            );
          })}

          {storeId && (
            <Box px={1.5} pt={1} pb={1} sx={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
              {payment.credit ? (
                <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                  <ConfirmationNumberOutlined fontSize='small' color='secondary' />
                  <Typography variant='body2' sx={{ flex: { xs: '1 0 100%', sm: '1 0 0' } }}>
                    Vale {payment.credit.code}
                    <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                      (máx ${ccyFormat(payment.credit.max_amount)})
                    </Typography>
                  </Typography>
                  <TextField
                    type='number'
                    size='small'
                    value={payment.credit.amount === 0 ? '' : payment.credit.amount}
                    onChange={(e) => handleCreditAmountChange(paymentIndex, Math.max(0, Number(e.target.value) || 0))}
                    InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }}
                    inputProps={{ min: 0, max: payment.credit.max_amount }}
                    sx={{ width: 140, flexShrink: 0 }}
                  />
                  <IconButton size='small' onClick={() => handleRemoveCreditDiscount(paymentIndex)} aria-label='Quitar vale'>
                    <DeleteOutline fontSize='small' />
                  </IconButton>
                </Box>
              ) : (
                <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                  <ConfirmationNumberOutlined fontSize='small' color='disabled' />
                  <Autocomplete
                    size='small'
                    sx={{ width: 280 }}
                    loading={loadingCredits}
                    options={availableCredits.filter((c) => !usedCreditCodes.has(c.code))}
                    getOptionLabel={(opt) => {
                      if (typeof opt === 'string') return opt;
                      const amt = opt.balance ?? opt.amount;
                      const amtLabel = amt === undefined || amt === null ? 'ver saldo' : `$${ccyFormat(amt)}`;
                      return `${opt.code} · ${amtLabel}${opt.name_sale ? ` · ${opt.name_sale}` : ''}`;
                    }}
                    isOptionEqualToValue={(opt, val) => opt.code === val.code}
                    noOptionsText={loadingCredits ? 'Cargando vales...' : 'No hay vales pendientes'}
                    inputValue={creditCodeInputs[paymentIndex] || ''}
                    onInputChange={(e, newInputValue) => setCreditCodeInputs((prev) => ({ ...prev, [paymentIndex]: newInputValue }))}
                    onChange={(e, selected) => { if (selected && typeof selected !== 'string') handleSelectCredit(paymentIndex, selected) }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder='Buscar vale por código'
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') { e.preventDefault(); handleSearchCredit(paymentIndex) }
                        }}
                      />
                    )}
                  />
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={creditLoading[paymentIndex]}
                    onClick={() => handleSearchCredit(paymentIndex)}
                  >
                    {creditLoading[paymentIndex] ? 'Buscando...' : 'Agregar vale'}
                  </Button>
                </Box>
              )}
              {creditErrors[paymentIndex] && (
                <Typography variant='caption' color='error' sx={{ display: 'block', mt: 0.5 }}>
                  {creditErrors[paymentIndex]}
                </Typography>
              )}
            </Box>
          )}

          {errors.payments?.[paymentIndex]?.amount && (
            <Box px={1.5} pb={1}>
              <Typography variant='caption' color='error'>{errors.payments[paymentIndex].amount}</Typography>
            </Box>
          )}
        </Box>
      ))}

      <Box display='flex' justifyContent='space-between' mt={0.5}>
        <Button
          variant='outlined'
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ width: { xs: '48%', sm: 'auto' } }}
        >
          Cancelar (Esc)
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!isValid || isSubmitting}
          onClick={handleSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color='inherit' /> : undefined}
          sx={{ width: { xs: '48%', sm: 'auto' } }}
        >
          {isSubmitting ? 'Confirmando...' : 'Confirmar (Enter)'}
        </Button>
      </Box>
    </Box>
  );
};

export default FormPaymentType;
